import * as React from "react";
import {inject, observer} from "mobx-react";

import classes from "./Clip.styl";
import {ServicesInitWrapper} from "@ok/web/components/base/utils/ServicesInitWrapper";
import {EmbeddingBehavior} from "OK/video/player-common";
import type {Clip as TClip, VideoPlayerNode} from "../../../lib/types";
import classNames from "classnames";
import {UniCell} from "@ok/common/design-system/components/UniCell";
import {Content} from "@ok/common/design-system/components/Content";
import {UserAvatar} from "@ok/web/components/base/Avatars/UserAvatar/UserAvatar";
import {Like} from "./Controls/Like";
import {Reshare} from "./Controls/Reshare";
import {Dislike} from "./Controls/Dislike";
import {More} from "./Controls/More";
import {ClipsFullscreenStore} from "../../../lib/ClipsFullscreenStore";
import {Subscribe} from "./Controls/Subscribe";
import {ClipLoadError} from "../ClipLoadError/ClipLoadError";

import {isFunction} from "OK/modules/shims/typeDetection";
import type {OneVideoPlayerElement} from "@ok/video-player";
import {Timeline} from "./Controls/Timeline";
import {Description} from "./Description/Description";


interface IClipProps {
    store?: ClipsFullscreenStore;
    bean: TClip;
    isCurrent: boolean;
    isNext: boolean;
    isPrev: boolean;
}


export const Clip: React.FC<IClipProps> = inject("store")(observer(
    (props): React.ReactElement => {
        const store = props.store!;

        const {
            authorInfo
        } = props.bean.payload;

        const {
            movie
        } = props.bean.meta;

        const attributes = {
            "data-options": props.bean.attrsAsString,
            "data-bundle-url": props.bean.bundleUrl,
            "data-embedding-behavior": EmbeddingBehavior.ON_PREPARED_VISIBLE,
            "data-is-autostart": false,
            "data-is-singleton": false,
            "data-callbacks-key": "clipsFullscreen"
        };

        const playerRef = React.useRef<VideoPlayerNode>({node: null});
        const rootRef = React.useRef<HTMLDivElement>(null);


        /* Т.к плеер вставляется в вёрстку через сервис, то делаем так,
        *  чтобы избежать ошибки гидарции
        * */
        const [isCSR, setIsCSR] = React.useState(false);
        React.useEffect(() => {
            setIsCSR(true);
        }, []);

        React.useEffect(() => {
            const ref = rootRef.current;
            const playerContainer = playerRef.current;

            if (!ref || !playerContainer || !isCSR) {
                return;
            }

            const playerContainerElement = ref.querySelector(".vk-video-player") as OneVideoPlayerElement;
            playerRef.current.node = playerContainerElement;

            return () => {
                if (!ref) {
                    return;
                }

                const playerContainer = ref.querySelector(".vk-video-player");

                //@ts-ignore
                if (playerContainer && isFunction(playerContainer.__selfDeactivate)) {
                    //@ts-ignore
                    playerContainer.__selfDeactivate();
                }
            };
        }, [isCSR]);

        React.useEffect(() => {
            const playerContainerElement = playerRef.current.node;

            if (!playerContainerElement || !props.isCurrent) {
                return;
            }
            if (!playerContainerElement.call) {
                return;
            }

            playerContainerElement.call("togglePlay", !store.isStopCurrentClip);
        }, [store.isStopCurrentClip, props.isCurrent]);


        function handleClickOnViewableSpace() {
            store.setIsStopCurrentClip(!store.isStopCurrentClip);
        }

        const cnControls = classNames(
            classes["clip__controls"],
            {
                [classes["__animated"]]: store.isVisible,
            }
        );

        const cnInfo = classNames(
            classes["clip__info"],
            {
                [classes["__animated"]]: store.isVisible,
            }
        );

        const containerCn = classNames(
            classes["vk-video-player-container"],
            "vk-video-player"
        );

        const clipCn = classNames(
            classes["clip"],
            {
                [classes["__isPrev"]]: props.isPrev,
                [classes["__isCurrent"]]: props.isCurrent,
                [classes["__isNext"]]: props.isNext,
                [classes["__disabled"]]: store.isNavigationRunning,
                [classes["__controls-animated"]]: store.isVisible
            }
        );

        let ownerName = "";

        if (authorInfo.firstName) {
            ownerName = authorInfo.firstName;

            if (authorInfo.isUser && authorInfo.lastName) {
                ownerName += " " + authorInfo.lastName;
            }
        }

        if (store.isLoadError && props.isCurrent) {
            return <ClipLoadError poster={movie.poster}/>;
        }

        return (
            <div ref={rootRef} className={classes["height100"]}>
                <div id={movie.movieId.toString()} className={clipCn}>
                    <div className={classes["clip__ui"]}>
                        <div className={classes["clip__main"]}>
                            <div className={classes["clip__view"]}>
                                <div className={classes["clip__window"]} onClick={handleClickOnViewableSpace}></div>
                                <div className={cnInfo}>
                                    <UniCell
                                        data-tsid={"clip-author-info"}
                                        view={"primary"}
                                        customVarsStyles={classes["reset"]}
                                        state={authorInfo.pageUrl ? "enabled" : "none"}
                                    >
                                        <UniCell.Left>
                                            <OkOwnerWrapper url={authorInfo.pageUrl}>
                                                <UserAvatar
                                                    lazy={false}
                                                    user={{
                                                        name: ownerName,
                                                        gender: 1,
                                                        imageUrl: authorInfo.avatar}}
                                                    size={"36"}
                                                />
                                            </OkOwnerWrapper>
                                        </UniCell.Left>
                                        <UniCell.Middle>
                                            <OkOwnerWrapper url={authorInfo.pageUrl}>
                                                <Content>
                                                    <Content.Text
                                                        view={"custom"}
                                                        size={"15b"}
                                                        maxLines={1}
                                                        customColorsStyles={classes["clip__owner"]}
                                                    >
                                                        {ownerName}
                                                    </Content.Text>
                                                </Content>
                                            </OkOwnerWrapper>
                                        </UniCell.Middle>

                                        <UniCell.Right>
                                            <Subscribe authorInfo={authorInfo}/>
                                        </UniCell.Right>
                                    </UniCell>

                                    <Description text={movie.title}/>
                                </div>
                            </div>
                            <div className={classes["clip__controls-wrapper"]}>
                                <div className={classes["clip__window"]} onClick={handleClickOnViewableSpace}></div>
                                <div className={cnControls}>
                                    <div className={classes["clip__control"]}>
                                        <div className={classes["control__button"]}>
                                            <Like bean={props.bean}/>
                                        </div>
                                    </div>
                                    <div className={classes["clip__control"]}>
                                        <div className={classes["control__button"]}>
                                            <Reshare bean={props.bean}/>
                                        </div>
                                    </div>
                                    <div className={classes["clip__control"]}>
                                        <div className={classes["control__button"]}>
                                            <Dislike bean={props.bean}/>
                                        </div>
                                    </div>
                                    <div className={classes["clip__control"]}>
                                        <div className={classes["control__button"]}>
                                            <More complaintLink={props.bean.payload.complaintLink}/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Timeline duration={movie.duration}
                                  playerContainerElement={playerRef}
                                  isCurrentClip={props.isCurrent}
                                  movieId={movie.id}
                        />
                    </div>

                    {isCSR && (
                        <ServicesInitWrapper services={"one-video-player"}
                                             attrs={attributes}
                                             className={containerCn}
                        />
                    )}
                </div>
            </div>
        );
    }));


interface IOkOwnerWrapper extends React.PropsWithChildren {
    url?: string;
}
const OkOwnerWrapper = (props: IOkOwnerWrapper) => {
    return (
        props.url ? (
            <a href={props.url} className={classes["owner-link"]}>{props.children}</a>
        ) : (
            <>{props.children}</>
        )
    );
};
